/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  ApiErrors,
  buildEmailRule,
  IFormValues,
  isShallowEqual,
  IUserModel,
  updateApiErrors,
  useConsentSelector,
  useDispatch,
  UserConsentsNames,
  UserStore,
  useUserSelector,
} from "@nf/common";
import cx from "classnames";
import { Trans, useTranslation } from "next-i18next";
import { useEffect, useState } from "react";

import ActionsHandler from "components/ActionsHandler";
import Checkbox from "components/Checkbox";
import CommonTextError from "components/CommonTextError";
import PersonalInformationInfo from "components/Form/PersonalInformationInfo";
import LinkText from "components/LinkText";
import { useUserCountry } from "hooks";
import { Form, FormButton, LabelField } from "../../../Form";
import { Input } from "../../../Inputs";
import { Country, PhoneInput } from "../../../PhoneInput";

import styles from "./ContactsForm.module.scss";

const FORM_FIELDS = ["firstName", "lastName", "email"];

export const ContactsForm = () => {
  const { t } = useTranslation("translations");
  const {
    profile: { Data: user, Error: userError, IsProcessing: isProfileLoading },
  } = useUserSelector();
  const {
    consent,
    isLoading: isConsentLoading,
    error: consentError,
  } = useConsentSelector(UserConsentsNames.Marketing);
  const dispatch = useDispatch();
  const [isFormSent, setIsFormSent] = useState(false);
  const [apiErrors, setApiErrors] = useState<ApiErrors>({});
  const [valuesChanged, setValuesChanged] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [form] = Form.useForm();
  const { data: userCountry } = useUserCountry();

  const error = userError ? userError : consentError;

  const initialValues: IFormValues = {
    email: user?.Email,
    firstName: user?.FirstName,
    lastName: user?.LastName,
    phone: {
      prefix: user?.PhoneNumberPrefix ?? "",
      number: user?.PhoneNumberNumber ?? "",
    },
  };

  const onValuesChange = (changedValues: IFormValues, values: IFormValues) => {
    const [isUpdated, newApiErrors] = updateApiErrors(apiErrors, changedValues);

    if (values.hasOwnProperty("consent")) {
      initialValues.consent = consent?.Accepted;
    }

    if (isUpdated) {
      setApiErrors(newApiErrors);
    }

    setApiErrors({
      ...apiErrors,
      UpdateProfile: [""],
    });

    checkIfChanged(!isShallowEqual(initialValues, values));

    if (Object.keys(changedValues)[0] === "email") {
      setEmailChanged(changedValues["email"] !== user?.Email);
    }
    isFormSent && setIsFormSent(false);
  };

  const checkIfChanged = (changed: boolean) =>
    changed ? setValuesChanged(true) : setValuesChanged(false);

  const onFinish = (values: IFormValues) => {
    const userPayload: Partial<IUserModel> = {
      FirstName: values["firstName"],
      LastName: values["lastName"],
      PhoneNumberPrefix: values["phone"]?.prefix || "",
      PhoneNumberNumber: values["phone"]?.number || "",
      Email: values["email"] !== user?.Email ? values["email"] : undefined,
    };

    if (consent) {
      const consentPayload = [
        {
          ...consent,
          Accepted: !!(values["consent"] ?? consent.Accepted),
          NeedUpdate: false,
        },
      ];
      dispatch(UserStore.Actions.updateUserConsents(consentPayload));
    }

    dispatch(UserStore.Actions.updateProfile(userPayload));
    setIsFormSent(true);
  };

  useEffect(() => {
    if (isFormSent && error) {
      setApiErrors({
        ...apiErrors,
        UpdateProfile: [error.Message as string],
      });
    }
  }, [error, isFormSent]);

  useEffect(() => {
    if (!user) dispatch(UserStore.Actions.getProfile());
  }, []);

  useEffect(() => {
    if (consent) {
      form.setFieldsValue({ consent: consent?.Accepted });
    }
  }, [consent]);

  const isLoading = isProfileLoading || isConsentLoading;
  const fetchedData = !isLoading || user;
  const isApiError =
    apiErrors.UpdateProfile && !!apiErrors.UpdateProfile[0]?.length;

  return (
    <ActionsHandler
      isLoading={isLoading && !fetchedData}
      error={!fetchedData || !user ? error : undefined}
      errorTranslationKey="web_my_account_error"
    >
      <>
        {user && (
          <Form
            form={form}
            className={styles.contactsForm}
            onValuesChange={onValuesChange}
            onFinish={onFinish}
            initialValues={initialValues}
          >
            <div className={styles.nameInputs}>
              <LabelField
                label={t("web_contacts_form_name_label", "")}
                name="firstName"
                validateTrigger={["onBlur", "onSubmit"]}
              >
                <Input maxLength={100} />
              </LabelField>
              <LabelField
                name="lastName"
                label={"\u00A0"}
                validateTrigger={["onBlur", "onSubmit"]}
              >
                <Input maxLength={100} />
              </LabelField>
            </div>
            <LabelField
              label={t("web_contacts_form_email_label", "")}
              name="email"
              tip={
                emailChanged ? t("web_contacts_form_email_tip", "") : undefined
              }
              rules={[buildEmailRule(t)]}
              validateTrigger={["onBlur", "onSubmit"]}
            >
              <Input className={cx({ [styles.emailChanged]: emailChanged })} />
            </LabelField>

            <LabelField
              name="phone"
              tip={t("web_contacts_form_phone_tip", "")}
              label={t("web_contacts_form_phone_label", "")}
              validateTrigger={["onBlur", "onSubmit"]}
            >
              <PhoneInput defaultCountry={userCountry as Country} />
            </LabelField>

            {consent && (
              <div className={styles.consent}>
                <Form.Field name="consent">
                  {({ onChange }) => (
                    <Checkbox
                      onChange={(e) => onChange(e.target.checked)}
                      defaultChecked={consent.Accepted}
                    />
                  )}
                </Form.Field>
                <Trans
                  t={t}
                  i18nKey="web_consent_marketing_label"
                  components={{
                    1: <p className={styles.consentText} />,
                    2: <LinkText href={t("marketing_consent_partner")} />,
                    bold: <strong />,
                  }}
                />
              </div>
            )}
            <FormButton
              nonEmptyFields={{
                requiredFields: FORM_FIELDS,
                autoCompleted: false,
              }}
              disabled={!valuesChanged || isFormSent}
              loading={isLoading}
            >
              {t("web_contacts_form_submit", "")}
            </FormButton>
            {!isLoading && isApiError && (
              <CommonTextError error={error} centered />
            )}

            {emailChanged && isFormSent && !isApiError && !isLoading && (
              <div className={styles.textContainer}>
                <Trans
                  t={t}
                  i18nKey="web_contacts_form_email_change"
                  tOptions={{ defaultValue: "" }}
                  components={{ 1: <p /> }}
                />
              </div>
            )}
            <div className={styles.textContainer}>
              <PersonalInformationInfo />
            </div>
          </Form>
        )}
      </>
    </ActionsHandler>
  );
};
